import { useCallback, useEffect, useMemo, useState, type ReactElement } from "react";
import {
  recordShiftOnlyDeposit,
  trackerApi,
  type TrackerClient,
  type TrackerContext,
  type TrackerWriteResult
} from "../lib/tracker-api.js";
import { todayDayKey, formatShortDateTime, type TimeZonePref } from "../lib/datetime.js";
import { useUiPrefs } from "../lib/ui-prefs.js";
import { useRealtimeStore } from "../lib/store.js";

// Трекер депозитов (ТЗ 2026-08-24): порт бота @TrafficManagersProdBot в клиент.
// В боте это пошаговый диалог, здесь — одна форма: саппорт заполняет всё сразу,
// видит сводку и подтверждает; до подтверждения любое поле можно поправить.
//
// Все пишущие операции идут через сводку (`pending`), у которой один
// externalOperationId на попытку: ретрай после сетевой ошибки не задваивает
// депозит, а второй клик по «Подтвердить» игнорируется админкой.

type TrackerMode = "create" | "deposit" | "state" | "edit";

const MODES: Array<{ id: TrackerMode; label: string; hint: string }> = [
  { id: "create", label: "Новый клиент", hint: "Карточка клиента и первый депозит" },
  { id: "deposit", label: "Повторный депозит", hint: "Следующий шаг существующему клиенту" },
  { id: "state", label: "Профит и статус", hint: "Прибыль и статус клиента" },
  { id: "edit", label: "Правка", hint: "Данные карточки и уже занесённые шаги" }
];

/** «Отзыв» — видео вместо оплаты: шаг пишется, деньги не считаются. */
const REVIEW_PAYMENT_METHOD = 7;

interface SummaryRow {
  label: string;
  value: string;
}

interface PendingOperation {
  title: string;
  rows: SummaryRow[];
  submit: () => Promise<TrackerWriteResult>;
  /**
   * Сумма депозита для запасного пути «только в смену». Показывается, если
   * админ-панель не ответила: карточка клиента подождёт, а ЗП смены — нет.
   */
  fallbackAmount?: number;
}

interface CreateForm {
  /** Бот новой карточки ("" — основной бот проекта). Подставляется из найденной заготовки. */
  botId: string;
  telegramId: string;
  username: string;
  nickname: string;
  siteLogin: string;
  contact: string;
  supportName: string;
  paymentMethod: number;
  amount: string;
  depositedAt: string;
  age: string;
  sessionHours: string;
  country: string;
  city: string;
}

interface DepositForm {
  stepNumber: string;
  supportName: string;
  paymentMethod: number;
  amount: string;
  depositedAt: string;
}

interface EditForm {
  telegramId: string;
  username: string;
  nickname: string;
  siteLogin: string;
  contact: string;
  supportName: string;
  age: string;
  sessionHours: string;
  country: string;
  city: string;
  status: number;
}

interface StepForm {
  stepId: number;
  stepNumber: string;
  paymentMethod: number;
  amount: string;
  profitAmount: string;
  depositedAt: string;
}

const emptyCreateForm = (supportName: string, today: string): CreateForm => ({
  botId: "",
  telegramId: "",
  username: "",
  nickname: "",
  siteLogin: "",
  contact: "",
  supportName,
  paymentMethod: 0,
  amount: "",
  depositedAt: today,
  age: "",
  sessionHours: "",
  country: "",
  city: ""
});

const toNumberOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const toIntOrNull = (value: string): number | null => {
  const parsed = toNumberOrNull(value);
  return parsed === null ? null : Math.trunc(parsed);
};

const trimmedOrNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
};

const newOperationId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const clientLabel = (client: TrackerClient): string => {
  const main =
    client.siteLogin ??
    client.nickname ??
    client.fullName ??
    (client.telegramId !== null ? String(client.telegramId) : `#${client.clientId}`);
  return client.username ? `${main} (@${client.username})` : main;
};

/**
 * Автозаполнение формы «Новый клиент» и авто-выбор в «Повторный депозит»,
 * когда модалка открывается из инфо-панели чата. Все поля опциональны.
 */
export interface TrackerModalPrefill {
  /** Telegram ID собеседника. Идёт в createForm И используется для авто-resolve в deposit. */
  telegramId?: string;
  /** @username собеседника. У саппорта поле не рендерится, но значение уходит на бэк. */
  username?: string;
  /** Отображаемое имя из Telegram (chat.title). */
  nickname?: string;
  /** ISO-код страны из phoneNumberCountry профиля. */
  country?: string;
}

export const DepositTrackerModal = ({
  wsUrl,
  onClose,
  onSaved,
  initialMode,
  initialPrefill,
  initialClientId
}: {
  wsUrl: string;
  onClose: () => void;
  /** Дёргается после записи, чтобы дашборд смены перечитал депозиты. */
  onSaved: () => void;
  /** Открыть сразу нужную вкладку (по умолчанию — «Новый клиент»). */
  initialMode?: TrackerMode;
  /** Автозаполнение из инфо-панели чата. */
  initialPrefill?: TrackerModalPrefill;
  /** Для deposit/state/edit: после загрузки контекста автоматически резолвнуть клиента. */
  initialClientId?: number;
}) => {
  const timeZone: TimeZonePref = useUiPrefs((state) => state.timeZone);
  const today = todayDayKey(timeZone);
  const appRole = useRealtimeStore((state) => state.appRole);
  // Саппорт не должен видеть @username клиента — поле в форме «Новый клиент»
  // не рендерится, но автозаполненное значение из profile всё равно уходит
  // на бэк, чтобы админка получила корректный username.
  const isSupportRole = appRole === "support";

  const [context, setContext] = useState<TrackerContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [geoId, setGeoId] = useState<string | null>(null);
  const [mode, setMode] = useState<TrackerMode>(initialMode ?? "create");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingOperation | null>(null);
  const [operationId, setOperationId] = useState<string>(newOperationId);
  const [fallbackDone, setFallbackDone] = useState(false);
  const [autoResolveDone, setAutoResolveDone] = useState(false);

  const [createForm, setCreateForm] = useState<CreateForm>(() => {
    const base = emptyCreateForm("", today);
    if (!initialPrefill) return base;
    return {
      ...base,
      telegramId: initialPrefill.telegramId ?? base.telegramId,
      username: initialPrefill.username ?? base.username,
      nickname: initialPrefill.nickname ?? base.nickname,
      country: initialPrefill.country ?? base.country
    };
  });

  const [searchTelegramId, setSearchTelegramId] = useState("");
  const [searchUsername, setSearchUsername] = useState("");
  // Поиск по номеру карточки (запрос №27): админка в отказе «уже есть»
  // называет только номер, а найти по нему было негде.
  const [searchClientId, setSearchClientId] = useState("");
  const [matches, setMatches] = useState<TrackerClient[]>([]);
  const [selected, setSelected] = useState<TrackerClient | null>(null);
  const [searchMessage, setSearchMessage] = useState<string | null>(null);

  const [depositForm, setDepositForm] = useState<DepositForm>({
    stepNumber: "2",
    supportName: "",
    paymentMethod: 0,
    amount: "",
    depositedAt: today
  });
  const [profitAmount, setProfitAmount] = useState("");
  const [statusValue, setStatusValue] = useState(0);
  const [editForm, setEditForm] = useState<EditForm | null>(null);
  const [stepForm, setStepForm] = useState<StepForm | null>(null);

  const paymentMethods = context?.paymentMethods ?? [];
  const statuses = context?.statuses ?? [];
  const binding = context?.binding ?? null;
  const currency = binding?.currency ?? "";

  const paymentLabel = useCallback(
    (value: number): string =>
      paymentMethods.find((method) => method.value === value)?.label ?? String(value),
    [paymentMethods]
  );
  const statusLabel = useCallback(
    (value: number): string =>
      statuses.find((status) => status.value === value)?.label ?? String(value),
    [statuses]
  );

  const loadContext = useCallback(
    async (requestedGeoId: string | null): Promise<void> => {
      setLoading(true);
      try {
        const next = await trackerApi.getContext(wsUrl, requestedGeoId);
        setContext(next);
        setGeoId(requestedGeoId ?? next.binding?.geoId ?? null);
        setCreateForm((current) =>
          current.supportName.trim().length === 0
            ? { ...current, supportName: next.supportName }
            : current
        );
        setDepositForm((current) =>
          current.supportName.trim().length === 0
            ? { ...current, supportName: next.supportName }
            : current
        );
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Не удалось загрузить трекер");
      } finally {
        setLoading(false);
      }
    },
    [wsUrl]
  );

  useEffect(() => {
    void loadContext(null);
  }, [loadContext]);

  const amountDisabled = (method: number): boolean => method === REVIEW_PAYMENT_METHOD;

  const resetAfterWrite = (result: TrackerWriteResult, what: string): void => {
    const client = result.client ?? null;
    const localNote =
      result.localEntry === null || result.localEntry === undefined
        ? ""
        : " · записано в «Мою смену»";
    setSuccess(
      result.duplicate
        ? `${what}: повторная отправка проигнорирована (уже занесено).`
        : `${what}${client ? `: ${clientLabel(client)}` : ""}${localNote}`
    );
    if (client) {
      setSelected(client);
      setMatches((current) =>
        current.map((item) => (item.clientId === client.clientId ? client : item))
      );
    }
    setPending(null);
    setOperationId(newOperationId());
    onSaved();
  };

  const confirmPending = async (): Promise<void> => {
    if (!pending) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await pending.submit();
      resetAfterWrite(result, pending.title);
    } catch (cause) {
      // Тот же operationId остаётся в силе: повтор после ошибки сети не задвоит запись.
      setError(cause instanceof Error ? cause.message : "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };

  const stagePending = (operation: PendingOperation): void => {
    setError(null);
    setSuccess(null);
    setFallbackDone(false);
    setPending(operation);
  };

  // Депозит только в смену: линия считает штуки — пишем 1, считает Тотал — сумму.
  const submitShiftOnly = async (amount: number): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const entryAmount = context?.salaryMetric === "deposits" ? 1 : amount;
      await recordShiftOnlyDeposit(wsUrl, entryAmount);
      setFallbackDone(true);
      setSuccess(
        "Депозит записан в «Мою смену». Карточку клиента занесите в трекер, когда админ-панель ответит."
      );
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Не удалось записать депозит в смену");
    } finally {
      setBusy(false);
    }
  };

  // --- Поиск клиента --------------------------------------------------------

  const runSearch = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setSearchMessage(null);
    try {
      const clientId = toIntOrNull(searchClientId);
      const result =
        clientId !== null && clientId > 0
          ? await trackerApi.getClient(wsUrl, clientId, geoId).then(({ client }) => ({
              matches: [client],
              message: `Карточка #${client.clientId} найдена`
            }))
          : await trackerApi.resolve(wsUrl, {
              telegramId: toIntOrNull(searchTelegramId),
              username: trimmedOrNull(searchUsername),
              geoId
            });
      setMatches(result.matches);
      setSearchMessage(result.message);
      const single = result.matches.length === 1 ? result.matches[0] : undefined;
      if (single) {
        selectClient(single);
      } else {
        setSelected(null);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Поиск не удался");
    } finally {
      setBusy(false);
    }
  };

  // Отказ «Такой клиент уже есть в базе (карточка #N)» (запрос №27): клиент
  // реально заведён — открываем его карточку в «Повторном депозите», а не
  // оставляем саппорта искать номер вручную.
  const existingCardId = (() => {
    const match = error ? /карточка #(\d+)/i.exec(error) : null;
    return match ? Number(match[1]) : null;
  })();

  const openExistingCard = async (clientId: number): Promise<void> => {
    setBusy(true);
    try {
      const { client } = await trackerApi.getClient(wsUrl, clientId, geoId);
      setMatches([client]);
      setSearchClientId(String(clientId));
      setSearchMessage(`Карточка #${client.clientId} — клиент уже в трекере`);
      selectClient(client);
      setMode("deposit");
      setError(null);
      setSuccess(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : `Не удалось открыть карточку #${clientId}`);
    } finally {
      setBusy(false);
    }
  };

  const selectClient = (client: TrackerClient): void => {
    setSelected(client);
    setPending(null);
    if (client.clientId === 0) {
      // Заготовка «юзер бота есть, карточки нет»: бот известен — подставляем
      // его и известные поля в «Новый клиент», чтобы карточка легла в тот бот,
      // откуда человек реально пришёл.
      setCreateForm((current) => ({
        ...current,
        botId: client.botId !== null ? String(client.botId) : current.botId,
        telegramId:
          current.telegramId.trim().length === 0 && client.telegramId !== null
            ? String(client.telegramId)
            : current.telegramId,
        username: current.username.trim().length === 0 ? (client.username ?? "") : current.username,
        nickname:
          current.nickname.trim().length === 0
            ? (client.nickname ?? client.fullName ?? "")
            : current.nickname
      }));
    }
    setDepositForm((current) => ({
      ...current,
      stepNumber: String(client.nextStepNumber),
      paymentMethod: client.lastPaymentMethod ?? current.paymentMethod,
      amount: "",
      depositedAt: today
    }));
    setProfitAmount(client.lastProfitAmount !== null ? String(client.lastProfitAmount) : "");
    setStatusValue(client.status);
    setEditForm({
      telegramId: client.telegramId !== null ? String(client.telegramId) : "",
      username: client.username ?? "",
      nickname: client.nickname ?? "",
      siteLogin: client.siteLogin ?? "",
      contact: client.contact ?? "",
      supportName: client.secondLineSupportName ?? client.firstLineSupportName ?? "",
      age: client.age !== null ? String(client.age) : "",
      sessionHours: client.sessionHours !== null ? String(client.sessionHours) : "",
      country: client.country ?? "",
      city: client.city ?? "",
      status: client.status
    });
    setStepForm(null);
  };

  // Открыта из инфо-панели чата с указанным клиентом: резолвим по Telegram ID
  // и выбираем нужную карточку, чтобы саппорт не искал вручную. Один раз за
  // жизнь модалки, после того как context.binding доступен.
  useEffect(() => {
    if (autoResolveDone) return;
    if (!binding) return;
    if (initialClientId === undefined || initialClientId <= 0) return;
    const telegramIdStr = initialPrefill?.telegramId;
    if (!telegramIdStr) {
      setAutoResolveDone(true);
      return;
    }
    const telegramId = toIntOrNull(telegramIdStr);
    if (telegramId === null) {
      setAutoResolveDone(true);
      return;
    }
    setAutoResolveDone(true);
    void (async () => {
      try {
        const result = await trackerApi.resolve(wsUrl, {
          telegramId,
          username: null,
          geoId
        });
        const target =
          result.matches.find((match) => match.clientId === initialClientId) ??
          result.matches.find((match) => match.clientId > 0) ??
          null;
        if (target) {
          selectClient(target);
        }
      } catch {
        // Тихо: саппорт может найти клиента через поиск, ошибку показывать
        // не нужно — авто-резолв это удобство, не критичный путь.
      }
    })();
    // selectClient пересоздаётся каждый рендер — сознательно исключаем из deps,
    // эффект должен сработать один раз при первом появлении binding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binding, autoResolveDone, initialClientId, initialPrefill?.telegramId, wsUrl, geoId]);

  // --- Сборка операций ------------------------------------------------------

  const stageCreate = (): void => {
    const amount = amountDisabled(createForm.paymentMethod)
      ? 0
      : toNumberOrNull(createForm.amount);
    if (!amountDisabled(createForm.paymentMethod) && (amount === null || amount <= 0)) {
      setError("Укажите сумму депозита");
      return;
    }
    if (trimmedOrNull(createForm.supportName) === null) {
      setError("Укажите, кто вёл клиента");
      return;
    }
    if (toIntOrNull(createForm.telegramId) === null && trimmedOrNull(createForm.username) === null) {
      setError("Укажите Telegram ID или @username клиента");
      return;
    }
    const opId = operationId;
    stagePending({
      title: "Клиент занесён",
      ...(amountDisabled(createForm.paymentMethod) ? {} : { fallbackAmount: amount ?? 0 }),
      rows: [
        { label: "Гео / бот", value: `${binding?.geoTitle ?? "—"} · ${createBotName}` },
        { label: "Telegram ID", value: createForm.telegramId.trim() || "—" },
        // Саппорт не видит @username: строка исчезает и из сводки тоже.
        ...(isSupportRole
          ? []
          : [{ label: "@username", value: createForm.username.trim() || "—" }]),
        { label: "Никнейм", value: createForm.nickname.trim() || "—" },
        { label: "Логин на сайте", value: createForm.siteLogin.trim() || "—" },
        { label: "Телефон", value: createForm.contact.trim() || "—" },
        { label: "Кто вёл", value: createForm.supportName.trim() },
        { label: "Способ оплаты", value: paymentLabel(createForm.paymentMethod) },
        {
          label: "Сумма 1-го депозита",
          value: amountDisabled(createForm.paymentMethod)
            ? "— (отзыв)"
            : `${amount ?? 0} ${currency}`.trim()
        },
        { label: "Дата", value: createForm.depositedAt || today },
        { label: "Возраст", value: createForm.age.trim() || "14 (по умолчанию)" },
        { label: "Сессия, часов", value: createForm.sessionHours.trim() || "—" },
        { label: "Страна", value: createForm.country.trim() || "Не указано" },
        { label: "Город", value: createForm.city.trim() || "Не указано" }
      ],
      submit: () =>
        trackerApi.createClient(wsUrl, {
          externalOperationId: opId,
          geoId,
          botId: toIntOrNull(createForm.botId),
          telegramId: toIntOrNull(createForm.telegramId),
          username: trimmedOrNull(createForm.username),
          nickname: trimmedOrNull(createForm.nickname),
          siteLogin: trimmedOrNull(createForm.siteLogin),
          contact: trimmedOrNull(createForm.contact),
          supportName: trimmedOrNull(createForm.supportName),
          paymentMethod: createForm.paymentMethod,
          amount: amount ?? 0,
          depositedAt: createForm.depositedAt || today,
          age: toIntOrNull(createForm.age),
          sessionHours: toIntOrNull(createForm.sessionHours),
          country: trimmedOrNull(createForm.country),
          city: trimmedOrNull(createForm.city)
        })
    });
  };

  const stageDeposit = (): void => {
    if (!selected) {
      return;
    }
    const amount = amountDisabled(depositForm.paymentMethod)
      ? 0
      : toNumberOrNull(depositForm.amount);
    if (!amountDisabled(depositForm.paymentMethod) && (amount === null || amount <= 0)) {
      setError("Укажите сумму депозита");
      return;
    }
    const stepNumber = toIntOrNull(depositForm.stepNumber);
    if (stepNumber === null || stepNumber < 2) {
      setError("Шаг повторного депозита — число от 2");
      return;
    }
    const opId = operationId;
    const clientId = selected.clientId;
    stagePending({
      title: "Депозит занесён",
      ...(amountDisabled(depositForm.paymentMethod) ? {} : { fallbackAmount: amount ?? 0 }),
      rows: [
        { label: "Клиент", value: clientLabel(selected) },
        { label: "Шаг", value: String(stepNumber) },
        { label: "2 линия", value: depositForm.supportName.trim() || context?.supportName || "—" },
        { label: "Способ оплаты", value: paymentLabel(depositForm.paymentMethod) },
        {
          label: "Сумма",
          value: amountDisabled(depositForm.paymentMethod)
            ? "— (отзыв)"
            : `${amount ?? 0} ${currency}`.trim()
        },
        { label: "Дата", value: depositForm.depositedAt || today }
      ],
      submit: () =>
        trackerApi.addDeposit(wsUrl, clientId, {
          externalOperationId: opId,
          geoId,
          stepNumber,
          paymentMethod: depositForm.paymentMethod,
          amount: amount ?? 0,
          supportName: trimmedOrNull(depositForm.supportName),
          depositedAt: depositForm.depositedAt || today
        })
    });
  };

  const stageProfit = (): void => {
    if (!selected) {
      return;
    }
    const amount = toNumberOrNull(profitAmount);
    if (amount === null || amount < 0) {
      setError("Укажите сумму прибыли");
      return;
    }
    const opId = operationId;
    const clientId = selected.clientId;
    stagePending({
      title: "Прибыль обновлена",
      rows: [
        { label: "Клиент", value: clientLabel(selected) },
        { label: "Прибыль", value: `${amount} ${currency}`.trim() }
      ],
      submit: () =>
        trackerApi.updateProfit(wsUrl, clientId, {
          externalOperationId: opId,
          geoId,
          profitAmount: amount
        })
    });
  };

  const stageStatus = (): void => {
    if (!selected) {
      return;
    }
    const opId = operationId;
    const clientId = selected.clientId;
    stagePending({
      title: "Статус обновлён",
      rows: [
        { label: "Клиент", value: clientLabel(selected) },
        { label: "Статус", value: statusLabel(statusValue) }
      ],
      submit: () =>
        trackerApi.updateStatus(wsUrl, clientId, {
          externalOperationId: opId,
          geoId,
          status: statusValue
        })
    });
  };

  const stageEdit = (): void => {
    if (!selected || !editForm) {
      return;
    }
    const opId = operationId;
    const clientId = selected.clientId;
    stagePending({
      title: "Карточка обновлена",
      rows: [
        { label: "Клиент", value: clientLabel(selected) },
        { label: "Telegram ID", value: editForm.telegramId.trim() || "—" },
        { label: "@username", value: editForm.username.trim() || "—" },
        { label: "Никнейм", value: editForm.nickname.trim() || "—" },
        { label: "Логин на сайте", value: editForm.siteLogin.trim() || "—" },
        { label: "Телефон", value: editForm.contact.trim() || "—" },
        { label: "Кто вёл", value: editForm.supportName.trim() || "—" },
        { label: "Возраст", value: editForm.age.trim() || "—" },
        { label: "Сессия, часов", value: editForm.sessionHours.trim() || "—" },
        { label: "Страна", value: editForm.country.trim() || "—" },
        { label: "Город", value: editForm.city.trim() || "—" },
        { label: "Статус", value: statusLabel(editForm.status) }
      ],
      submit: () =>
        trackerApi.editClient(wsUrl, clientId, {
          externalOperationId: opId,
          geoId,
          telegramId: toIntOrNull(editForm.telegramId),
          username: trimmedOrNull(editForm.username),
          nickname: trimmedOrNull(editForm.nickname),
          siteLogin: trimmedOrNull(editForm.siteLogin),
          contact: trimmedOrNull(editForm.contact),
          supportName: trimmedOrNull(editForm.supportName),
          age: toIntOrNull(editForm.age),
          sessionHours: toIntOrNull(editForm.sessionHours),
          country: trimmedOrNull(editForm.country),
          city: trimmedOrNull(editForm.city),
          status: editForm.status
        })
    });
  };

  const stageStepUpdate = (): void => {
    if (!selected || !stepForm) {
      return;
    }
    const amount = amountDisabled(stepForm.paymentMethod) ? 0 : toNumberOrNull(stepForm.amount);
    if (!amountDisabled(stepForm.paymentMethod) && (amount === null || amount <= 0)) {
      setError("Укажите сумму шага");
      return;
    }
    const stepNumber = toIntOrNull(stepForm.stepNumber);
    if (stepNumber === null || stepNumber < 1) {
      setError("Номер шага — число от 1");
      return;
    }
    const opId = operationId;
    const clientId = selected.clientId;
    const stepId = stepForm.stepId;
    stagePending({
      title: "Шаг обновлён",
      rows: [
        { label: "Клиент", value: clientLabel(selected) },
        { label: "Шаг", value: String(stepNumber) },
        { label: "Способ оплаты", value: paymentLabel(stepForm.paymentMethod) },
        {
          label: "Сумма",
          value: amountDisabled(stepForm.paymentMethod)
            ? "— (отзыв)"
            : `${amount ?? 0} ${currency}`.trim()
        },
        { label: "Прибыль", value: stepForm.profitAmount.trim() || "без изменений" },
        { label: "Дата", value: stepForm.depositedAt || "без изменений" }
      ],
      submit: () =>
        trackerApi.updateStep(wsUrl, clientId, stepId, {
          externalOperationId: opId,
          geoId,
          stepNumber,
          paymentMethod: stepForm.paymentMethod,
          amount: amount ?? 0,
          profitAmount: toNumberOrNull(stepForm.profitAmount),
          depositedAt: stepForm.depositedAt || null
        })
    });
  };

  // --- Рендер ---------------------------------------------------------------

  const geoOptions = useMemo(() => context?.geos ?? [], [context?.geos]);
  const projectBots = binding?.bots ?? [];
  // Архивные боты проекта: их клиентов ищем и ведём, но новых в них не заводим.
  const selectableBots = projectBots.filter((bot) => !bot.isArchived);
  const multiBot = projectBots.length > 1;
  const createBotName =
    projectBots.find((bot) => String(bot.botId) === createForm.botId)?.botName ??
    binding?.botName ??
    "—";

  const renderField = (
    label: string,
    input: ReactElement,
    hint?: string
  ): ReactElement => (
    <label className="auth-field tracker-modal__field">
      <span className="auth-field__label">{label}</span>
      {input}
      {hint ? <span className="tracker-modal__hint">{hint}</span> : null}
    </label>
  );

  const paymentSelect = (value: number, onChange: (next: number) => void): ReactElement => (
    <select value={value} onChange={(event) => onChange(Number(event.target.value))}>
      {paymentMethods.map((method) => (
        <option key={method.value} value={method.value}>
          {method.label}
        </option>
      ))}
    </select>
  );

  const clientCard = (client: TrackerClient): ReactElement => (
    <div className="tracker-modal__client">
      <strong>{clientLabel(client)}</strong>
      {multiBot && client.botName ? <span>Бот: {client.botName}</span> : null}
      {client.clientId === 0 ? (
        <span>
          Пользователь бота есть, но карточки трекера ещё нет — заведите её во вкладке
          «Новый клиент».
        </span>
      ) : null}
      {client.clientId > 0 ? (
        <span>
          Карточка #{client.clientId} · шагов {client.totalSteps} · вложено{" "}
          {client.totalInvestments} {currency}
        </span>
      ) : null}
      <span>
        Статус: {statusLabel(client.status)} · прибыль {client.projectBalance} {currency}
        {client.lastDepositAt
          ? ` · последний депозит ${formatShortDateTime(client.lastDepositAt, timeZone)}`
          : ""}
      </span>
      {client.deeplinkTag ? <span>Источник: {client.deeplinkTag}</span> : null}
    </div>
  );

  const searchBlock = (): ReactElement => (
    <section className="tracker-modal__section">
      <div className="tracker-modal__grid">
        {renderField(
          "Telegram ID клиента",
          <input
            value={searchTelegramId}
            onChange={(event) => setSearchTelegramId(event.target.value)}
            placeholder="Например 123456789"
            inputMode="numeric"
          />
        )}
        {renderField(
          "@username",
          <input
            value={searchUsername}
            onChange={(event) => setSearchUsername(event.target.value)}
            placeholder="@client"
          />
        )}
        {renderField(
          "№ карточки",
          <input
            value={searchClientId}
            onChange={(event) => setSearchClientId(event.target.value)}
            placeholder="6885"
            inputMode="numeric"
          />,
          "Номер из сообщения «такой клиент уже есть в базе»"
        )}
      </div>
      <div className="tracker-modal__actions">
        <button
          type="button"
          className="button"
          onClick={() => void runSearch()}
          disabled={
            busy ||
            !binding ||
            (toIntOrNull(searchClientId) === null &&
              toIntOrNull(searchTelegramId) === null &&
              trimmedOrNull(searchUsername) === null)
          }
        >
          {busy ? "Ищем…" : "Найти клиента"}
        </button>
        {searchMessage ? <span className="tracker-modal__hint">{searchMessage}</span> : null}
      </div>
      {matches.length > 1 ? (
        <div className="tracker-modal__matches">
          {matches.map((client) => (
            <button
              type="button"
              key={`${client.botId ?? 0}:${client.clientId}:${client.telegramId ?? client.username ?? ""}`}
              className={
                selected?.clientId === client.clientId
                  ? "button button--primary"
                  : "button button--ghost"
              }
              onClick={() => selectClient(client)}
            >
              {clientLabel(client)}
              {multiBot && client.botName ? ` · ${client.botName}` : ""}
            </button>
          ))}
        </div>
      ) : null}
      {selected ? clientCard(selected) : null}
    </section>
  );

  const createBlock = (): ReactElement => (
    <section className="tracker-modal__section">
      <div className="tracker-modal__grid">
        {selectableBots.length > 1 && binding
          ? renderField(
              "Бот (откуда пришёл клиент)",
              <select
                value={createForm.botId}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, botId: event.target.value }))
                }
              >
                {selectableBots.map((bot, index) => (
                  <option key={bot.botId} value={index === 0 ? "" : String(bot.botId)}>
                    {bot.botName}
                    {index === 0 ? " (основной)" : ""}
                  </option>
                ))}
              </select>,
              "Если клиент нашёлся поиском, бот подставился сам"
            )
          : null}
        {renderField(
          "Telegram ID клиента",
          <input
            value={createForm.telegramId}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, telegramId: event.target.value }))
            }
            inputMode="numeric"
            placeholder="123456789"
          />
        )}
        {isSupportRole
          ? null
          : renderField(
              "@username",
              <input
                value={createForm.username}
                onChange={(event) =>
                  setCreateForm((current) => ({ ...current, username: event.target.value }))
                }
                placeholder="@client"
              />
            )}
        {renderField(
          "Никнейм в Telegram",
          <input
            value={createForm.nickname}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, nickname: event.target.value }))
            }
          />
        )}
        {renderField(
          "Логин на сайте",
          <input
            value={createForm.siteLogin}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, siteLogin: event.target.value }))
            }
          />
        )}
        {renderField(
          "Телефон",
          <input
            value={createForm.contact}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, contact: event.target.value }))
            }
          />
        )}
        {renderField(
          "Кто вёл",
          <input
            value={createForm.supportName}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, supportName: event.target.value }))
            }
          />
        )}
        {renderField(
          "Способ оплаты",
          paymentSelect(createForm.paymentMethod, (next) =>
            setCreateForm((current) => ({ ...current, paymentMethod: next }))
          )
        )}
        {renderField(
          `Сумма 1-го депозита${currency ? `, ${currency}` : ""}`,
          <input
            type="number"
            min={0}
            step={0.01}
            value={amountDisabled(createForm.paymentMethod) ? "" : createForm.amount}
            disabled={amountDisabled(createForm.paymentMethod)}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, amount: event.target.value }))
            }
          />,
          amountDisabled(createForm.paymentMethod) ? "Отзыв — сумма не заносится" : undefined
        )}
        {renderField(
          "Дата депозита",
          <input
            type="date"
            value={createForm.depositedAt}
            max={today}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, depositedAt: event.target.value }))
            }
          />
        )}
        {renderField(
          "Возраст",
          <input
            type="number"
            min={14}
            max={120}
            value={createForm.age}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, age: event.target.value }))
            }
          />,
          "Пусто — 14 по умолчанию"
        )}
        {renderField(
          "Сессия, часов",
          <input
            type="number"
            min={0}
            max={72}
            value={createForm.sessionHours}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, sessionHours: event.target.value }))
            }
          />
        )}
        {renderField(
          "Страна",
          <input
            value={createForm.country}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, country: event.target.value }))
            }
          />
        )}
        {renderField(
          "Город",
          <input
            value={createForm.city}
            onChange={(event) =>
              setCreateForm((current) => ({ ...current, city: event.target.value }))
            }
          />
        )}
      </div>
      <div className="tracker-modal__actions">
        <button
          type="button"
          className="button button--primary"
          onClick={stageCreate}
          disabled={busy || !binding}
        >
          Проверить и занести
        </button>
        <button
          type="button"
          className="button button--ghost"
          onClick={() => setCreateForm(emptyCreateForm(context?.supportName ?? "", today))}
          disabled={busy}
        >
          Очистить
        </button>
      </div>
    </section>
  );

  const depositBlock = (): ReactElement => (
    <>
      {searchBlock()}
      {selected && selected.clientId > 0 ? (
        <section className="tracker-modal__section">
          {selected.hasFirstDeposit ? null : (
            <div className="tracker-modal__warning">
              У клиента нет первого депозита — заведите его во вкладке «Новый клиент».
            </div>
          )}
          <div className="tracker-modal__grid">
            {renderField(
              "Шаг",
              <input
                type="number"
                min={2}
                max={100}
                value={depositForm.stepNumber}
                onChange={(event) =>
                  setDepositForm((current) => ({ ...current, stepNumber: event.target.value }))
                }
              />,
              `Следующий по карточке — ${selected.nextStepNumber}`
            )}
            {renderField(
              "Саппорт 2 линии",
              <input
                value={depositForm.supportName}
                onChange={(event) =>
                  setDepositForm((current) => ({ ...current, supportName: event.target.value }))
                }
              />
            )}
            {renderField(
              "Способ оплаты",
              paymentSelect(depositForm.paymentMethod, (next) =>
                setDepositForm((current) => ({ ...current, paymentMethod: next }))
              )
            )}
            {renderField(
              `Сумма${currency ? `, ${currency}` : ""}`,
              <input
                type="number"
                min={0}
                step={0.01}
                value={amountDisabled(depositForm.paymentMethod) ? "" : depositForm.amount}
                disabled={amountDisabled(depositForm.paymentMethod)}
                onChange={(event) =>
                  setDepositForm((current) => ({ ...current, amount: event.target.value }))
                }
              />,
              amountDisabled(depositForm.paymentMethod) ? "Отзыв — сумма не заносится" : undefined
            )}
            {renderField(
              "Дата депозита",
              <input
                type="date"
                value={depositForm.depositedAt}
                max={today}
                onChange={(event) =>
                  setDepositForm((current) => ({ ...current, depositedAt: event.target.value }))
                }
              />
            )}
          </div>
          <div className="tracker-modal__actions">
            <button
              type="button"
              className="button button--primary"
              onClick={stageDeposit}
              disabled={busy || !selected.hasFirstDeposit}
            >
              Проверить и занести
            </button>
          </div>
        </section>
      ) : null}
    </>
  );

  const stateBlock = (): ReactElement => (
    <>
      {searchBlock()}
      {selected && selected.clientId > 0 ? (
        <section className="tracker-modal__section">
          <div className="tracker-modal__grid">
            {renderField(
              `Сумма прибыли${currency ? `, ${currency}` : ""}`,
              <input
                type="number"
                min={0}
                step={0.01}
                value={profitAmount}
                onChange={(event) => setProfitAmount(event.target.value)}
              />,
              "Заменяет прибыль последнего депозита"
            )}
            {renderField(
              "Статус клиента",
              <select
                value={statusValue}
                onChange={(event) => setStatusValue(Number(event.target.value))}
              >
                {statuses.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="tracker-modal__actions">
            <button type="button" className="button" onClick={stageProfit} disabled={busy}>
              Обновить прибыль
            </button>
            <button type="button" className="button" onClick={stageStatus} disabled={busy}>
              Обновить статус
            </button>
          </div>
        </section>
      ) : null}
    </>
  );

  const editBlock = (): ReactElement => (
    <>
      {searchBlock()}
      {selected && selected.clientId > 0 && editForm ? (
        <>
          <section className="tracker-modal__section">
            <div className="tracker-modal__grid">
              {renderField(
                "Telegram ID",
                <input
                  value={editForm.telegramId}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, telegramId: event.target.value } : current
                    )
                  }
                  inputMode="numeric"
                />
              )}
              {renderField(
                "@username",
                <input
                  value={editForm.username}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, username: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Никнейм",
                <input
                  value={editForm.nickname}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, nickname: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Логин на сайте",
                <input
                  value={editForm.siteLogin}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, siteLogin: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Телефон",
                <input
                  value={editForm.contact}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, contact: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Кто вёл",
                <input
                  value={editForm.supportName}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, supportName: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Возраст",
                <input
                  type="number"
                  min={14}
                  max={120}
                  value={editForm.age}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, age: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Сессия, часов",
                <input
                  type="number"
                  min={0}
                  max={72}
                  value={editForm.sessionHours}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, sessionHours: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Страна",
                <input
                  value={editForm.country}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, country: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Город",
                <input
                  value={editForm.city}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, city: event.target.value } : current
                    )
                  }
                />
              )}
              {renderField(
                "Статус",
                <select
                  value={editForm.status}
                  onChange={(event) =>
                    setEditForm((current) =>
                      current ? { ...current, status: Number(event.target.value) } : current
                    )
                  }
                >
                  {statuses.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="tracker-modal__actions">
              <button
                type="button"
                className="button button--primary"
                onClick={stageEdit}
                disabled={busy}
              >
                Проверить и сохранить
              </button>
            </div>
          </section>

          <section className="tracker-modal__section">
            <h4 className="tracker-modal__subtitle">Занесённые шаги</h4>
            {selected.steps.length === 0 ? (
              <p className="tracker-modal__hint">Шагов пока нет.</p>
            ) : (
              <div className="tracker-modal__steps">
                {selected.steps.map((step) => (
                  <div key={step.stepId} className="tracker-modal__step">
                    <span>
                      Шаг {step.stepNumber} · {step.amount} {currency} ·{" "}
                      {paymentLabel(step.paymentMethod)} ·{" "}
                      {formatShortDateTime(step.createdAt, timeZone)}
                      {step.supportName ? ` · ${step.supportName}` : ""}
                    </span>
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() =>
                        setStepForm({
                          stepId: step.stepId,
                          stepNumber: String(step.stepNumber),
                          paymentMethod: step.paymentMethod,
                          amount: String(step.amount),
                          profitAmount: String(step.profitAmount),
                          depositedAt: ""
                        })
                      }
                    >
                      Править
                    </button>
                  </div>
                ))}
              </div>
            )}
            {stepForm ? (
              <div className="tracker-modal__grid">
                {renderField(
                  "Номер шага",
                  <input
                    type="number"
                    min={1}
                    max={100}
                    value={stepForm.stepNumber}
                    onChange={(event) =>
                      setStepForm((current) =>
                        current ? { ...current, stepNumber: event.target.value } : current
                      )
                    }
                  />
                )}
                {renderField(
                  "Способ оплаты",
                  paymentSelect(stepForm.paymentMethod, (next) =>
                    setStepForm((current) =>
                      current ? { ...current, paymentMethod: next } : current
                    )
                  )
                )}
                {renderField(
                  `Сумма${currency ? `, ${currency}` : ""}`,
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={amountDisabled(stepForm.paymentMethod) ? "" : stepForm.amount}
                    disabled={amountDisabled(stepForm.paymentMethod)}
                    onChange={(event) =>
                      setStepForm((current) =>
                        current ? { ...current, amount: event.target.value } : current
                      )
                    }
                  />
                )}
                {renderField(
                  `Прибыль${currency ? `, ${currency}` : ""}`,
                  <input
                    type="number"
                    min={0}
                    step={0.01}
                    value={stepForm.profitAmount}
                    onChange={(event) =>
                      setStepForm((current) =>
                        current ? { ...current, profitAmount: event.target.value } : current
                      )
                    }
                  />
                )}
                {renderField(
                  "Дата шага",
                  <input
                    type="date"
                    value={stepForm.depositedAt}
                    max={today}
                    onChange={(event) =>
                      setStepForm((current) =>
                        current ? { ...current, depositedAt: event.target.value } : current
                      )
                    }
                  />,
                  "Пусто — дату не меняем"
                )}
              </div>
            ) : null}
            {stepForm ? (
              <div className="tracker-modal__actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={stageStepUpdate}
                  disabled={busy}
                >
                  Проверить и сохранить шаг
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => setStepForm(null)}
                  disabled={busy}
                >
                  Отмена
                </button>
              </div>
            ) : null}
          </section>
        </>
      ) : null}
    </>
  );

  return (
    <div
      className="forward-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Трекер депозитов"
    >
      <button
        type="button"
        className="forward-modal__backdrop"
        onClick={onClose}
        aria-label="Закрыть"
      />
      <div className="forward-modal__panel tracker-modal">
        <div className="forward-sheet__header">
          <div>
            <strong>Трекер депозитов</strong>
            <p>
              {binding
                ? `Гео ${binding.geoTitle}${
                    binding.workspaceName ? ` · проект «${binding.workspaceName}»` : ""
                  } · ${
                    multiBot
                      ? `боты: ${projectBots.map((bot) => bot.botName).join(", ")}`
                      : `бот «${binding.botName ?? binding.botId}»`
                  }${binding.currency ? ` · ${binding.currency}` : ""}`
                : "Гео не привязано к проекту админ-панели"}
            </p>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>

        {geoOptions.length > 0 ? (
          <label className="auth-field tracker-modal__geo">
            <span className="auth-field__label">Гео</span>
            <select
              value={geoId ?? ""}
              onChange={(event) => {
                const next = event.target.value || null;
                setSelected(null);
                setMatches([]);
                void loadContext(next);
              }}
            >
              <option value="">— моё гео —</option>
              {geoOptions.map((geo) => (
                <option key={geo.geoId} value={geo.geoId}>
                  {geo.title}
                  {geo.configured ? "" : " (проект не выбран)"}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className="tracker-modal__tabs">
          {MODES.map((item) => (
            <button
              type="button"
              key={item.id}
              className={mode === item.id ? "button button--primary" : "button button--ghost"}
              title={item.hint}
              onClick={() => {
                setMode(item.id);
                setPending(null);
                setError(null);
                setSuccess(null);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="tracker-modal__body">
          {loading ? <p className="tracker-modal__hint">Загружаем трекер…</p> : null}
          {context && !context.enabled ? (
            <div className="tracker-modal__warning">
              Интеграция с админ-панелью не настроена — обратитесь к овнеру.
            </div>
          ) : null}
          {context?.reason && !binding ? (
            <div className="tracker-modal__warning">{context.reason}</div>
          ) : null}
          {error ? <div className="shift-dash__error">{error}</div> : null}
          {existingCardId !== null ? (
            <div className="tracker-modal__actions">
              <button
                type="button"
                className="button button--primary"
                onClick={() => void openExistingCard(existingCardId)}
                disabled={busy}
              >
                Открыть карточку #{existingCardId} → «Повторный депозит»
              </button>
            </div>
          ) : null}
          {success ? <div className="tracker-modal__success">{success}</div> : null}

          {pending ? (
            <section className="tracker-modal__section tracker-modal__confirm">
              <h4 className="tracker-modal__subtitle">Проверьте данные</h4>
              <dl className="tracker-modal__summary">
                {pending.rows.map((row) => (
                  <div key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="tracker-modal__actions">
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => void confirmPending()}
                  disabled={busy}
                >
                  {busy ? "Сохраняем…" : "Подтвердить занесение"}
                </button>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => setPending(null)}
                  disabled={busy}
                >
                  Вернуться к правке
                </button>
                {error && pending.fallbackAmount !== undefined && !fallbackDone ? (
                  <button
                    type="button"
                    className="button"
                    onClick={() => void submitShiftOnly(pending.fallbackAmount ?? 0)}
                    disabled={busy}
                    title="Админ-панель не ответила: записать депозит только в «Мою смену» для расчёта ЗП"
                  >
                    Записать только в смену
                  </button>
                ) : null}
              </div>
            </section>
          ) : (
            <>
              {mode === "create" ? createBlock() : null}
              {mode === "deposit" ? depositBlock() : null}
              {mode === "state" ? stateBlock() : null}
              {mode === "edit" ? editBlock() : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DepositTrackerModal;
